import api from 'api/instance';

const URL = `${process.env.REACT_APP_API_URL}`;

export type WorkerStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'paused'
  | 'cancelled';

export type WorkerType = 'order_delivery' | 'create_package' | 'pickup';

export interface Worker {
  _id: string;
  type: WorkerType;
  status: WorkerStatus;
  washHub: string;
  totalUnits: number;
  completedUnits: number;
  progress: number;
  metadata: {
    orderId?: string;
    packageSku?: string;
    caseSku?: string;
    itemSkus?: string[];
    pickupId?: string;
    performedBy: string;
    batchSize?: number;
  };
  counts?: {
    items: { total: number; processed: number; unprocessed: number };
    cases: { total: number; processed: number; unprocessed: number };
    packages: { total: number; processed: number; unprocessed: number };
  };
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  lastHeartbeat?: string;
  error?: {
    message: string;
    failedJobId?: string;
    failedBatchId?: string;
  };
  pausedAt?: string;
  pauseReason?: string;
}

export interface WorkerJob {
  _id: string;
  worker: string;
  type: string;
  status: WorkerStatus;
  sequenceNumber: number;
  dependsOn?: string;
  totalBatches: number;
  completedBatches: number;
  totalRecords: number;
  completedRecords: number;
  payload: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  attempts: number;
  maxAttempts: number;
  lastError?: string;
}

export interface WorkerJobBatch {
  _id: string;
  workerJob: string;
  worker: string;
  batchNumber: number;
  status: WorkerStatus;
  recordIds: string[];
  operation: string;
  targetCollection: string;
  processedCount: number;
  skippedCount: number;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  attempts: number;
  maxAttempts: number;
  lastError?: string;
}

export interface ListWorkersParams {
  page?: number;
  limit?: number;
  status?: WorkerStatus[];
  type?: WorkerType[];
  washHub?: string;
}

export interface ListWorkersResponse {
  workers: Worker[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface WorkerDetailsResponse {
  worker: Worker;
  jobs: WorkerJob[];
  pausedBatches: WorkerJobBatch[];
}

export interface WorkerHealth {
  _id: string;
  active: boolean;
  consecutiveFailures: number;
  maxFailures: number;
  lastFailedAt?: string;
  lastFailureError?: string;
  lastFailedWorkerId?: string;
  deactivatedAt?: string;
  reactivatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PausedWorker extends Worker {
  pausedBatches: WorkerJobBatch[];
}

export const fetchWorkers = async (
  params: ListWorkersParams,
): Promise<ListWorkersResponse> => {
  const { data } = await api.get(`${URL}/worker-admin/list`, { params });

  return data.item;
};

export const fetchWorkerDetails = async (
  workerId: string,
): Promise<WorkerDetailsResponse> => {
  const { data } = await api.get(`${URL}/worker-admin/${workerId}`);

  return data.item;
};

export const resetPausedWorker = async (
  workerId: string,
): Promise<{ workerId: string; batchesReset: number }> => {
  const { data } = await api.post(`${URL}/worker-admin/${workerId}/reset`);

  return data.item;
};

export const fetchWorkerHealth = async (): Promise<WorkerHealth> => {
  const { data } = await api.get(`${URL}/worker-admin/health`);

  return data.item.health;
};

export const resumeBatchProcessing = async (): Promise<WorkerHealth> => {
  const { data } = await api.post(`${URL}/worker-admin/health/resume`);

  return data.item.health;
};

export const fetchPausedWorkers = async (): Promise<PausedWorker[]> => {
  const { data } = await api.get(`${URL}/worker-admin/paused`);

  return data.item.workers;
};

export const resolveWorker = async (
  workerId: string,
): Promise<{ workerId: string; batchesSkipped: number }> => {
  const { data } = await api.post(`${URL}/worker-admin/${workerId}/resolve`);

  return data.item;
};

export const skipBatch = async ({
  workerId,
  batchId,
}: {
  workerId: string;
  batchId: string;
}): Promise<{ workerId: string; batchId: string; recordsSkipped: number }> => {
  const { data } = await api.post(
    `${URL}/worker-admin/${workerId}/skip-batch/${batchId}`,
  );

  return data.item;
};

export const cancelWorker = async (
  workerId: string,
): Promise<{
  workerId: string;
  batchesRolledBack: number;
  itemsReverted: number;
}> => {
  const { data } = await api.post(`${URL}/worker-admin/${workerId}/cancel`);

  return data.item;
};
